// src/routes/Home.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FiArrowRight,
  FiArrowUpRight,
  FiCheck,
} from "react-icons/fi";

import Layout from "../components/Layout";
import ProductCard from "../components/ProductCard";
import FilterBar, { applyFilters } from "../components/FilterBar";
import HeroShadeDial from "../components/HeroShadeDial";
import useReveal from "../hooks/useReveal";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { useLang } from "../context/LanguageContext";
import {
  getProducts,
  getCategories,
  inCategory,
  displayName,
  productCode,
} from "../services/db";

gsap.registerPlugin(ScrollTrigger);

const HOME_PAGE_SIZE = 8;

/* Placeholder swatches for the hero dial, shown only before Firestore
   loads or if fewer than six products have a colour set. */
const FALLBACK_SHADES = [
  { hex: "#D8B98C", nameSq: "Blond" },
  { hex: "#9C7A55", nameSq: "Dark Blond" },
  { hex: "#B5702A", nameSq: "Light Brown" },
  { hex: "#6E4A24", nameSq: "Brown" },
  { hex: "#3B2B1E", nameSq: "Dark Brown" },
  { hex: "#241A14", nameSq: "Black Brown" },
  { hex: "#8C8C8C", nameSq: "Light Grey" },
  { hex: "#1A1A1A", nameSq: "Deep Black" },
  { hex: "#B87C7C", nameSq: "Antique Rose" },
  { hex: "#E8618F", nameSq: "Powder Pink" },
  { hex: "#D4295A", nameSq: "Rose Red" },
  { hex: "#E33C22", nameSq: "Mars Red" },
  { hex: "#B01836", nameSq: "Cherry Red" },
  { hex: "#6E1436", nameSq: "Bordeaux" },
  { hex: "#E5B2A0", nameSq: "Skin Tone" },
  { hex: "#1B5E3A", nameSq: "Olive Green" },
  { hex: "#1F3A73", nameSq: "Midnight Blue" },
];

/* ------------------------------------------------------------------ */
/*  Section content. Photos live in /public/home/ — drop a file in with  */
/*  the matching name and it replaces the soft placeholder by itself.    */
/* ------------------------------------------------------------------ */

/* Each logo has its own proportions, so each gets its own height to
   look the same visual size side by side. */
const PARTNERS = [
  { key: "labina", logo: "/home/partner-labina.png", name: "LaBina", h: 72 },
  { key: "vision", logo: "/home/partner-visionblue.png", name: "Vision blue", h: 68 },
  { key: "blessed", logo: "/home/partner-blessed.png", name: "Blessed Beauty", h: 84 },
];

const TRAININGS = [
  { key: "pmu", img: "/home/training-pmu.jpg", tone: 0 },
  { key: "remover", img: "/home/training-remover.jpg", tone: 1 },
  { key: "needle", img: "/home/training-needle.jpg", tone: 2 },
];

/* `kw` finds the matching category (or, failing that, a product search)
   so each tile opens the shop already filtered. Nothing is hard-coded to a
   category id, so renaming or adding categories in admin can't break it. */
const PRODUCT_TILES = [
  { key: "labina", img: "/home/product-labina.jpg", kw: ["labina", "pigment"] },
  {
    key: "needle",
    img: "/home/product-needle.jpg",
    kw: ["needle", "gjilp", "modul", "nadel", "vision"],
  },
  { key: "blessed", img: "/home/product-blessed.jpg", kw: ["blessed"] },
  {
    key: "aftercare",
    img: "/home/product-aftercare.jpg",
    kw: ["aftercare", "after care", "pflege"],
  },
];

const SERVICES = [
  { key: "lips", img: "/home/service-lips.jpg", tone: 0 },
  { key: "brows", img: "/home/service-brows.jpg", tone: 1 },
  { key: "lashes", img: "/home/service-lashes.jpg", tone: 2 },
  { key: "nails", img: "/home/service-nails.jpg", tone: 3 },
];

const STUDIO_IMG = "/home/studio.jpg";

/* Placeholder tones — warm blush for light cards, deeper rose for cards
   with white text on top. */
const LIGHT_TONES = [
  "linear-gradient(135deg, #F4E4DF 0%, #E8CFC7 100%)",
  "linear-gradient(135deg, #F1E6E1 0%, #DCC6BC 100%)",
  "linear-gradient(135deg, #F5E9E6 0%, #E6D3D6 100%)",
  "linear-gradient(135deg, #EFE7E2 0%, #DDD0C8 100%)",
];
const DEEP_TONES = [
  "linear-gradient(160deg, #D9A6A0 0%, #8F5A57 100%)",
  "linear-gradient(160deg, #B99484 0%, #5E4337 100%)",
  "linear-gradient(160deg, #A8908A 0%, #4E3E3B 100%)",
  "linear-gradient(160deg, #E5C4BC 0%, #A97D74 100%)",
];

export default function Home() {
  const { t, lang } = useLang();
  const { hash } = useLocation();
  useDocumentTitle("meta.home");

  const heroRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sort, setSort] = useState("code");
  const [shown, setShown] = useState(HOME_PAGE_SIZE);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, c] = await Promise.all([getProducts(), getCategories()]);
        if (!alive) return;
        setProducts(p.filter((x) => x.active !== false));
        setCategories(c);
      } catch (err) {
        console.error("Home load failed", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const shades = useMemo(() => {
    const fromDb = products
      .filter((p) => p.hex)
      .map((p) => ({
        id: p.id,
        hex: p.hex,
        nameSq: displayName(p, lang),
        code: productCode(p),
      }));
    return fromDb.length >= 6 ? fromDb : FALLBACK_SHADES;
  }, [products, lang]);

  const filtered = useMemo(
    () => applyFilters(products, { search, categoryId, sort }),
    [products, search, categoryId, sort],
  );

  const visible = filtered.slice(0, shown);
  const visibleCount = visible.length;
  const remaining = filtered.length - visibleCount;

  useEffect(() => {
    setShown(HOME_PAGE_SIZE);
  }, [search, categoryId, sort]);

  /* Product tiles: link + fallback photo resolved from live data. */
  const tiles = useMemo(
    () =>
      PRODUCT_TILES.map((tile) => {
        const catText = (c) =>
          [c.nameSq, c.nameDe, c.nameEn, c.id]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        const cat = categories.find((c) =>
          tile.kw.some((k) => catText(c).includes(k)),
        );

        let to = "/produktet";
        let list = [];

        if (cat) {
          to = `/produktet?kategoria=${encodeURIComponent(cat.id)}`;
          list = products.filter((p) => inCategory(p, cat.id));
        } else {
          for (const k of tile.kw) {
            const hits = applyFilters(products, {
              search: k,
              categoryId: "",
              sort: "code",
            });
            if (hits.length) {
              to = `/produktet?q=${encodeURIComponent(k)}`;
              list = hits;
              break;
            }
          }
        }

        const cover = list.find((p) => p.images?.[0])?.images[0] || null;
        return { ...tile, to, cover };
      }),
    [products, categories],
  );

  const scope = useReveal({ deps: [loading, visibleCount] });

  /* Arriving from another page via /#trajnime or /#sherbime. Runs again
     once products load, because the shop grid above changes height. */
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash, loading]);

  /* ---------------- hero entrance ---------------- */
  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap
        .timeline({ defaults: { ease: "expo.out" } })
        .from(".hero-line span", {
          yPercent: 108,
          duration: 1.05,
          stagger: 0.08,
        })
        .from(
          ".hero-meta",
          { opacity: 0, y: 16, duration: 0.7, stagger: 0.07 },
          "-=0.7",
        )
        .from(
          ".hero-rule",
          { scaleX: 0, duration: 1, ease: "power3.inOut" },
          "-=0.8",
        );

      gsap.to(".dial-scale", {
        yPercent: -18,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });

      // decorative drift only — the text stays readable the whole way
      gsap.to(".hero-inner", {
        yPercent: -10,
        opacity: 0.35,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 0.8,
        },
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <Layout padTop={false}>
      <div ref={scope}>
        {/* ---------------- hero ---------------- */}
        <Hero ref={heroRef}>
          <HeroInner className="hero-inner">
            <HeroTop className="hero-meta">
              <span>Kaida Beauty Studio</span>
              <span>Kosovë</span>
            </HeroTop>

            <Display>
              <Line className="hero-line">
                <span>{t("home.heroLine1")}</span>
              </Line>
              <Line className="hero-line">
                <span>{t("home.heroLine2")}</span>
              </Line>
              <Line className="hero-line">
                <span>
                  {t("home.heroLine3a")} <Accent>{t("home.heroLine3b")}</Accent>
                </span>
              </Line>
            </Display>

            <Rule className="hero-rule" />

            <HeroFoot>
              <HeroText className="hero-meta">{t("home.heroLead")}</HeroText>

              <HeroActions className="hero-meta">
                <Primary to="/produktet">
                  {t("home.heroShop")} <FiArrowRight />
                </Primary>
                <Ghost href="#studio">
                  {t("home.heroStudio")} <FiArrowUpRight />
                </Ghost>
              </HeroActions>
            </HeroFoot>
          </HeroInner>

          <HeroShadeDial shades={shades} />
        </Hero>

        {/* ---------------- partners ---------------- */}
        <Partners>
          <Container>
            <PartnersGrid>
              <div>
                <Eyebrow className="reveal">{t("home.partnersLabel")}</Eyebrow>
                <Logos className="reveal">
                  {PARTNERS.map((p) => (
                    <LogoCell key={p.key}>
                      <PartnerLogo partner={p} />
                    </LogoCell>
                  ))}
                </Logos>
              </div>

              <Exclusive className="reveal">
                <ExclusiveTitle>{t("home.exclusiveTitle")}</ExclusiveTitle>
                <p>{t("home.exclusiveText")}</p>
              </Exclusive>
            </PartnersGrid>
          </Container>
        </Partners>

        {/* ---------------- trainings ---------------- */}
        <Band id="trajnime">
          <Container>
            <TrainingGrid>
              <TrainingIntro>
                <Eyebrow className="reveal">
                  {t("home.trainingsEyebrow")}
                </Eyebrow>
                <SectionTitle className="reveal">
                  {t("home.trainingsTitleA")}{" "}
                  <Accent>{t("home.trainingsTitleB")}</Accent>
                </SectionTitle>
                <Outline to="/kontakt" className="reveal">
                  {t("home.trainingsCta")} <FiArrowRight />
                </Outline>
              </TrainingIntro>

              <TrainingCards data-reveal-stagger>
                {TRAININGS.map((tr) => {
                  const base = `home.trainings.${tr.key}`;
                  return (
                    <TrainingCard key={tr.key} to="/kontakt" className="reveal">
                      <Media
                        $ratio="16 / 8.5"
                        $src={tr.img}
                        $fallback={LIGHT_TONES[tr.tone]}
                        aria-hidden="true"
                      />
                      <CardBody>
                        <CardTitle>{t(`${base}.title`)}</CardTitle>
                        <Checks>
                          <li>
                            <FiCheck /> {t(`${base}.p1`)}
                          </li>
                          <li>
                            <FiCheck /> {t(`${base}.p2`)}
                          </li>
                        </Checks>
                        <CardArrow aria-hidden="true">
                          <FiArrowRight />
                        </CardArrow>
                      </CardBody>
                    </TrainingCard>
                  );
                })}
              </TrainingCards>
            </TrainingGrid>
          </Container>
        </Band>

        {/* ---------------- product categories ---------------- */}
        <Band $tone="cream">
          <Container>
            <SectionHead>
              <div>
                <Eyebrow className="reveal">{t("home.productsEyebrow")}</Eyebrow>
                <SectionTitle className="reveal">
                  {t("home.productsTitle")}
                </SectionTitle>
              </div>
              <TextLink to="/produktet" className="reveal">
                {t("home.productsAll")} <FiArrowRight />
              </TextLink>
            </SectionHead>

            <FourGrid data-reveal-stagger>
              {tiles.map((tile, i) => (
                <ProductTile key={tile.key} to={tile.to} className="reveal">
                  <Media
                    $ratio="16 / 9.5"
                    $src={tile.img}
                    $cover={tile.cover}
                    $fallback={LIGHT_TONES[i % LIGHT_TONES.length]}
                    aria-hidden="true"
                  />
                  <TileLabel>{t(`home.tiles.${tile.key}`)}</TileLabel>
                </ProductTile>
              ))}
            </FourGrid>
          </Container>
        </Band>

        {/* ---------------- shop (order straight from the homepage) ------ */}
        <Band id="dyqani">
          <Container>
            <SectionHead>
              <div>
                <Eyebrow className="reveal">{t("home.shopEyebrow")}</Eyebrow>
                <SectionTitle className="reveal">
                  {t("home.shopTitle")}
                </SectionTitle>
              </div>
              <TextLink to="/produktet" className="reveal">
                {t("home.seeAll")} <FiArrowRight />
              </TextLink>
            </SectionHead>

            <FilterWrap className="reveal">
              <FilterBar
                search={search}
                onSearch={setSearch}
                categories={categories}
                categoryId={categoryId}
                onCategory={setCategoryId}
                sort={sort}
                onSort={setSort}
                resultCount={loading ? undefined : filtered.length}
              />
            </FilterWrap>

            {loading ? (
              <Grid>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} />
                ))}
              </Grid>
            ) : visible.length ? (
              <>
                <Grid data-reveal-stagger>
                  {visible.map((p) => (
                    <ProductCard key={p.id} product={p} className="reveal" />
                  ))}
                </Grid>

                {remaining > 0 && (
                  <MoreWrap>
                    <MoreBtn onClick={() => setShown((n) => n + HOME_PAGE_SIZE)}>
                      {t("common.showMore")}
                      <MoreCount>
                        +{Math.min(remaining, HOME_PAGE_SIZE)}
                      </MoreCount>
                    </MoreBtn>
                    <MoreNote>
                      {t("common.ofProducts", {
                        shown: visibleCount,
                        total: filtered.length,
                      })}
                    </MoreNote>
                  </MoreWrap>
                )}

                {remaining === 0 && filtered.length > HOME_PAGE_SIZE && (
                  <MoreWrap>
                    <MoreNote>
                      {t("common.allShown", { count: filtered.length })}
                    </MoreNote>
                  </MoreWrap>
                )}
              </>
            ) : (
              <EmptyState className="reveal">
                <p>{t("home.noResults")}</p>
                <button
                  onClick={() => {
                    setSearch("");
                    setCategoryId("");
                  }}
                >
                  {t("common.clearFilters")}
                </button>
              </EmptyState>
            )}
          </Container>
        </Band>

        {/* ---------------- services ---------------- */}
        <Band id="sherbime" $tone="cream">
          <Container>
            <SectionHead>
              <div>
                <Eyebrow className="reveal">{t("home.servicesEyebrow")}</Eyebrow>
                <SectionTitle className="reveal">
                  {t("home.servicesTitle")}
                </SectionTitle>
              </div>
              <TextLink to="/kontakt" className="reveal">
                {t("home.servicesCta")} <FiArrowRight />
              </TextLink>
            </SectionHead>

            <FourGrid data-reveal-stagger>
              {SERVICES.map((s) => (
                <ServiceCard key={s.key} to="/kontakt" className="reveal">
                  <Media
                    $ratio="16 / 9.5"
                    $src={s.img}
                    $fallback={DEEP_TONES[s.tone]}
                    aria-hidden="true"
                  />
                  <ServiceShade aria-hidden="true" />
                  <ServiceLabel>
                    <span>{t(`home.services.${s.key}`)}</span>
                    <FiArrowRight aria-hidden="true" />
                  </ServiceLabel>
                </ServiceCard>
              ))}
            </FourGrid>
          </Container>
        </Band>

        {/* ---------------- visit the studio ---------------- */}
        <Visit id="studio">
          <VisitMedia
            $src={STUDIO_IMG}
            $fallback="linear-gradient(120deg, #F3E6E1 0%, #E4CDC4 55%, #D9BDB3 100%)"
            aria-hidden="true"
          />
          <VisitBody>
            <Eyebrow className="reveal">{t("home.visitKicker")}</Eyebrow>
            <VisitTitle className="reveal">{t("home.visitTitle")}</VisitTitle>
            <VisitText className="reveal">{t("home.visitText")}</VisitText>
            <PinkButton to="/kontakt" className="reveal">
              {t("home.visitCta")} <FiArrowRight />
            </PinkButton>
          </VisitBody>
        </Visit>
      </div>
    </Layout>
  );
}

/* ------------------------------------------------------------------ */
/*  Partner logo: real file if it exists, typeset wordmark otherwise    */
/* ------------------------------------------------------------------ */

function PartnerLogo({ partner }) {
  const [failed, setFailed] = useState(false);

  if (!failed) {
    return (
      <LogoImg
        src={partner.logo}
        alt={partner.name}
        style={{ height: partner.h }}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  if (partner.key === "labina") {
    return (
      <Wordmark aria-label={partner.name}>
        <WmSerif>
          LaBina24<sup>®</sup>
        </WmSerif>
        <WmSmall>German Beauty</WmSmall>
      </Wordmark>
    );
  }
  if (partner.key === "vision") {
    return (
      <Wordmark aria-label={partner.name} $row>
        <WmScript>Vision</WmScript>
        <WmBlue>blue</WmBlue>
      </Wordmark>
    );
  }
  return (
    <Wordmark aria-label={partner.name}>
      <WmCaps>Blessed Beauty</WmCaps>
      <WmSmall>Cosmetics</WmSmall>
    </Wordmark>
  );
}

/* ------------------------------------------------------------------ */
/*  shared                                                             */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.gutter};
`;

const Band = styled.section`
  padding: clamp(56px, 6.5vw, 96px) 0;
  scroll-margin-top: ${({ theme }) => theme.layout.navHeight};
  background: ${({ $tone, theme }) =>
    $tone === "cream" ? theme.color.bgCream : theme.color.bg};
  ${({ $tone, theme }) =>
    $tone === "cream" &&
    css`
      border-block: 1px solid ${theme.color.lineWarm};
    `}
`;

const Eyebrow = styled.p`
  margin: 0 0 14px;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  font-weight: ${({ theme }) => theme.weight.semi};
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 3.4vw, 3.1rem);
  line-height: 1.08;
`;

const Accent = styled.em`
  font-style: normal;
  color: ${({ theme }) => theme.color.brand};
`;

const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: clamp(24px, 3vw, 36px);

  ${({ theme }) => theme.down.md} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TextLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.size.sm};
  color: ${({ theme }) => theme.color.ink};
  transition: color 0.25s;

  /* the pink rule sits under the text only, not the arrow */
  background-image: linear-gradient(
    ${({ theme }) => theme.color.brand},
    ${({ theme }) => theme.color.brand}
  );
  background-size: calc(100% - 28px) 1px;
  background-position: 0 100%;
  background-repeat: no-repeat;
  padding-bottom: 4px;

  svg {
    font-size: 1.05rem;
    transition: transform 0.3s ${({ theme }) => theme.ease.out};
  }

  &:hover {
    color: ${({ theme }) => theme.color.brand};
    svg {
      transform: translateX(4px);
    }
  }
`;

/* Photo slot. Layers: the real photo, then an optional product photo,
   then a soft gradient. A missing file simply lets the next layer show. */
const Media = styled.div`
  aspect-ratio: ${({ $ratio }) => $ratio || "16 / 10"};
  background-color: #f1e7e3;
  background-image: ${({ $src, $cover, $fallback }) =>
    [
      $src && `url("${$src}")`,
      $cover && `url("${$cover}")`,
      $fallback,
    ]
      .filter(Boolean)
      .join(", ")};
  background-size: ${({ $src, $cover }) =>
    [$src && "cover", $cover && "auto 80%", "cover"].filter(Boolean).join(", ")};
  background-position: center;
  background-repeat: no-repeat;
  transition: transform 0.7s ${({ theme }) => theme.ease.out};
`;

const btnBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 48px;
  padding: 0 28px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  transition:
    background 0.3s,
    border-color 0.3s,
    color 0.3s,
    transform 0.3s ${({ theme }) => theme.ease.out};

  svg {
    transition: transform 0.3s ${({ theme }) => theme.ease.out};
  }
  &:hover {
    transform: translateY(-2px);
  }
  &:hover svg {
    transform: translateX(3px);
  }
`;

const Primary = styled(Link)`
  ${btnBase}
  min-height: 54px;
  padding: 0 30px;
  background: ${({ theme }) => theme.color.ink};
  color: #fff;

  &:hover {
    background: ${({ theme }) => theme.color.brand};
  }
`;

const Ghost = styled.a`
  ${btnBase}
  min-height: 54px;
  padding: 0 30px;
  border: 1px solid ${({ theme }) => theme.color.line};
  background: transparent;
  color: ${({ theme }) => theme.color.ink};

  &:hover {
    border-color: ${({ theme }) => theme.color.ink};
  }
`;

const Outline = styled(Link)`
  ${btnBase}
  min-height: 42px;
  padding: 0 22px;
  border: 1px solid ${({ theme }) => theme.color.brand};
  color: ${({ theme }) => theme.color.ink};

  &:hover {
    background: ${({ theme }) => theme.color.brandSoft};
  }
`;

const PinkButton = styled(Link)`
  ${btnBase}
  min-height: 44px;
  padding: 0 24px;
  background: ${({ theme }) => theme.color.brand};
  color: #fff;

  &:hover {
    background: ${({ theme }) => theme.color.brandDark};
  }
`;

/* ------------------------------------------------------------------ */
/*  hero — oversized type, lots of air, one accent, the shade dial      */
/* ------------------------------------------------------------------ */

const Hero = styled.section`
  position: relative;
  min-height: 100svh;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  background: ${({ theme }) => theme.color.bgCream};
  padding: calc(${({ theme }) => theme.layout.navHeight} + 40px)
    ${({ theme }) => theme.layout.gutter} clamp(40px, 6vh, 80px);
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
`;

const HeroTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: clamp(40px, 8vh, 90px);
  font-size: 0.72rem;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.muted};
`;

const Display = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.heading};
  font-size: clamp(3.1rem, 10.5vw, 9.6rem);
  font-weight: ${({ theme }) => theme.weight.medium};
  line-height: 0.95;
  letter-spacing: -0.055em;
`;

const Line = styled.span`
  display: block;
  /* The mask clips anything outside the box, and a line-height under 1 puts
     descenders (g, j, q, ç) outside it. Pad the box to fit the tails, then
     pull the same amount back off the margin so line spacing is unchanged. */
  overflow: hidden;
  padding-bottom: 0.18em;
  margin-bottom: -0.18em;

  span {
    display: block;
  }
`;

const Rule = styled.div`
  height: 1px;
  width: 100%;
  margin: clamp(36px, 6vh, 64px) 0 32px;
  background: ${({ theme }) => theme.color.lineWarm};
  transform-origin: left;
`;

const HeroFoot = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 40px;

  ${({ theme }) => theme.down.md} {
    flex-direction: column;
    align-items: flex-start;
    gap: 28px;
  }
`;

const HeroText = styled.p`
  margin: 0;
  max-width: 42ch;
  font-size: ${({ theme }) => theme.size.lg};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.body};
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  flex-shrink: 0;
`;

/* ------------------------------------------------------------------ */
/*  partners                                                           */
/* ------------------------------------------------------------------ */

const Partners = styled.section`
  background: ${({ theme }) => theme.color.bgBlush};
  padding: clamp(36px, 4vw, 52px) 0;
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: clamp(28px, 4vw, 64px);

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr;
  }
`;

const Logos = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  margin-top: 8px;
`;

const LogoCell = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  padding: 0 clamp(12px, 2vw, 28px);

  &:first-child {
    justify-content: flex-start;
    padding-left: 0;
  }
  & + & {
    border-left: 1px solid rgba(12, 10, 9, 0.12);
  }

  /* phones: all three stay on one row, just smaller */
  ${({ theme }) => theme.down.md} {
    justify-content: center;
    min-height: 56px;
    padding: 0 10px;
    &:first-child {
      justify-content: center;
    }
  }
`;

const LogoImg = styled.img`
  max-width: 100%;
  width: auto;
  object-fit: contain;

  ${({ theme }) => theme.down.md} {
    max-height: 44px;
  }
`;

const Wordmark = styled.span`
  display: inline-flex;
  flex-direction: ${({ $row }) => ($row ? "row" : "column")};
  align-items: ${({ $row }) => ($row ? "flex-end" : "center")};
  gap: ${({ $row }) => ($row ? "2px" : "6px")};
  line-height: 1;
  color: ${({ theme }) => theme.color.ink};
`;

const WmSerif = styled.span`
  font-family: ${({ theme }) => theme.font.heading};
  font-size: clamp(1.6rem, 2.2vw, 2rem);

  sup {
    font-size: 0.4em;
    vertical-align: top;
  }
`;

const WmSmall = styled.span`
  font-size: 0.56rem;
  letter-spacing: 0.42em;
  text-transform: uppercase;
`;

const WmScript = styled.span`
  font-family: ${({ theme }) => theme.font.heading};
  font-style: italic;
  font-size: clamp(1.8rem, 2.4vw, 2.2rem);
  color: ${({ theme }) => theme.color.brand};
`;

const WmBlue = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: ${({ theme }) => theme.weight.semi};
  font-size: 1.15rem;
  color: #1f7fd1;
  padding-bottom: 3px;
`;

const WmCaps = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: ${({ theme }) => theme.weight.medium};
  font-size: 1.05rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Exclusive = styled.div`
  max-width: 36ch;
  padding-left: clamp(28px, 4vw, 56px);
  border-left: 1px solid rgba(12, 10, 9, 0.12);

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.size.sm};
    color: ${({ theme }) => theme.color.body};
  }

  ${({ theme }) => theme.down.lg} {
    max-width: none;
    padding: 22px 0 0;
    border-left: 0;
    border-top: 1px solid rgba(12, 10, 9, 0.12);
  }
`;

const ExclusiveTitle = styled.p`
  && {
    margin: 0 0 14px;
    max-width: 26ch;
    font-size: 0.72rem;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color.ink};
  }
`;

/* ------------------------------------------------------------------ */
/*  trainings                                                          */
/* ------------------------------------------------------------------ */

const TrainingGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 0.85fr) 2.6fr;
  gap: clamp(28px, 4vw, 56px);
  align-items: start;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr;
  }
`;

const TrainingIntro = styled.div`
  ${SectionTitle} {
    margin-bottom: clamp(24px, 3vw, 36px);
  }
`;

const TrainingCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(14px, 1.6vw, 22px);

  /* On phones the three cards swipe sideways instead of stacking into
     one very long column. */
  ${({ theme }) => theme.down.md} {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: 78%;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    margin: 0 calc(-1 * ${({ theme }) => theme.layout.gutter});
    padding: 0 ${({ theme }) => theme.layout.gutter} 6px;
    scroll-padding: 0 ${({ theme }) => theme.layout.gutter};
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    & > * {
      scroll-snap-align: start;
    }
  }
`;

const cardBase = css`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.color.lineWarm};
  transition:
    box-shadow 0.35s ${({ theme }) => theme.ease.out},
    transform 0.35s ${({ theme }) => theme.ease.out};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadow.md};
  }
  &:hover ${Media} {
    transform: scale(1.04);
  }
`;

const TrainingCard = styled(Link)`
  ${cardBase}
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 18px 18px 16px;
`;

const CardTitle = styled.h3`
  margin: 0 0 12px;
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 1.02rem;
  font-weight: ${({ theme }) => theme.weight.medium};
  letter-spacing: -0.01em;
`;

const Checks = styled.ul`
  display: grid;
  gap: 6px;
  margin-bottom: 16px;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.color.body};
  }
  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.color.brand};
  }
`;

const CardArrow = styled.span`
  margin-top: auto;
  color: ${({ theme }) => theme.color.ink};
  transition: transform 0.3s ${({ theme }) => theme.ease.out};

  ${TrainingCard}:hover & {
    transform: translateX(4px);
    color: ${({ theme }) => theme.color.brand};
  }
`;

/* ------------------------------------------------------------------ */
/*  product tiles + services                                           */
/* ------------------------------------------------------------------ */

const FourGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(14px, 2vw, 24px);

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ProductTile = styled(Link)`
  ${cardBase}
`;

const TileLabel = styled.span`
  padding: 14px 16px;
  font-size: 0.82rem;
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.ink};
  border-top: 1px solid ${({ theme }) => theme.color.lineWarm};
`;

const ServiceCard = styled(Link)`
  position: relative;
  display: block;
  overflow: hidden;
  border-radius: 14px;
  /* keeps the rounded corners crisp while the photo scales on hover */
  isolation: isolate;

  &:hover ${Media} {
    transform: scale(1.05);
  }
`;

const ServiceShade = styled.span`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(12, 10, 9, 0.5) 0%,
    rgba(12, 10, 9, 0) 55%
  );
  pointer-events: none;
`;

const ServiceLabel = styled.span`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  font-size: clamp(0.95rem, 1.2vw, 1.1rem);
  font-weight: ${({ theme }) => theme.weight.medium};

  svg {
    font-size: 1.15rem;
    transition: transform 0.3s ${({ theme }) => theme.ease.out};
  }
  ${ServiceCard}:hover & svg {
    transform: translateX(4px);
  }
`;

/* ------------------------------------------------------------------ */
/*  visit banner                                                       */
/* ------------------------------------------------------------------ */

const Visit = styled.section`
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  background: ${({ theme }) => theme.color.bgBlush};
  scroll-margin-top: ${({ theme }) => theme.layout.navHeight};

  ${({ theme }) => theme.down.md} {
    grid-template-columns: 1fr;
  }
`;

const VisitMedia = styled.div`
  min-height: clamp(260px, 30vw, 420px);
  background-image: ${({ $src, $fallback }) => `url("${$src}"), ${$fallback}`};
  background-size: cover, cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const VisitBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(40px, 5vw, 72px) ${({ theme }) => theme.layout.gutter};
`;

const VisitTitle = styled.h2`
  max-width: 14ch;
  font-size: clamp(1.9rem, 3vw, 2.8rem);
  line-height: 1.08;
`;

const VisitText = styled.p`
  margin: 16px 0 26px;
  max-width: 36ch;
  font-size: ${({ theme }) => theme.size.sm};
  color: ${({ theme }) => theme.color.body};
`;

/* ------------------------------------------------------------------ */
/*  shop grid (unchanged behaviour)                                    */
/* ------------------------------------------------------------------ */

const FilterWrap = styled.div`
  margin-bottom: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${({ theme }) => theme.color.lineSoft};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: repeat(3, 1fr);
  }
  ${({ theme }) => theme.down.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const Skeleton = styled.div`
  aspect-ratio: 1 / 1.28;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: linear-gradient(
    100deg,
    ${({ theme }) => theme.color.bgSoft} 30%,
    ${({ theme }) => theme.color.lineSoft} 50%,
    ${({ theme }) => theme.color.bgSoft} 70%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s infinite linear;

  @keyframes shimmer {
    to {
      background-position: -220% 0;
    }
  }
`;

const MoreWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 48px;
`;

const MoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 0 16px 0 30px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.color.line};
  background: ${({ theme }) => theme.color.bg};
  color: ${({ theme }) => theme.color.ink};
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  transition: all 0.28s ${({ theme }) => theme.ease.out};

  &:hover {
    border-color: ${({ theme }) => theme.color.ink};
    background: ${({ theme }) => theme.color.ink};
    color: #fff;
    transform: translateY(-2px);
  }
`;

const MoreCount = styled.span`
  padding: 5px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.bgSoft};
  color: ${({ theme }) => theme.color.body};
  font-size: ${({ theme }) => theme.size.xs};
  font-weight: ${({ theme }) => theme.weight.semi};
  transition:
    background 0.28s,
    color 0.28s;

  ${MoreBtn}:hover & {
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
  }
`;

const MoreNote = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
`;

const EmptyState = styled.div`
  padding: 70px 0;
  text-align: center;
  color: ${({ theme }) => theme.color.muted};

  button {
    margin-top: 10px;
    color: ${({ theme }) => theme.color.brand};
    font-weight: ${({ theme }) => theme.weight.semi};
    text-decoration: underline;
    text-underline-offset: 4px;
  }
`;
